import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import Lightbox from 'yet-another-react-lightbox';
import Captions from 'yet-another-react-lightbox/plugins/captions';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/captions.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';

const RecapsPage = () => {
  const [photos, setPhotos] = useState([]);
  const [status, setStatus] = useState('loading'); // loading | error | ready
  const [lightboxIndex, setLightboxIndex] = useState(-1);

  useEffect(() => {
    api.get('/photos')
      .then((r) => { setPhotos(r.data); setStatus('ready'); })
      .catch(() => setStatus('error'));
  }, []);

  // Group by event, then flatten back out so each photo has a stable
  // global index the lightbox can navigate across.
  const groups = useMemo(() => {
    const acc = {};
    photos.forEach((p) => {
      const key = p.eventTitle || 'General';
      if (!acc[key]) acc[key] = [];
      acc[key].push(p);
    });
    return Object.entries(acc);
  }, [photos]);

  const orderedPhotos = useMemo(
    () => groups.flatMap(([, eventPhotos]) => eventPhotos),
    [groups]
  );

  const slides = useMemo(
    () => orderedPhotos.map((p) => ({
      src: p.url,
      title: p.eventTitle || undefined,
      description: p.caption || undefined,
    })),
    [orderedPhotos]
  );

  if (status === 'loading') {
    return (
      <div className="page-container">
        <div className="empty-state"><p>Loading...</p></div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="page-container">
        <div className="empty-state">
          <div className="empty-state-icon">⚠️</div>
          <h3>Couldn't load photos</h3>
          <p>Something went wrong fetching the recaps. Please try again later.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1 className="page-title">Tournament Recaps</h1>
        <p className="page-subtitle">Photos from past Nine &amp; Dine events</p>
      </div>

      {photos.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📸</div>
          <h3>No photos yet</h3>
          <p>Event photos will appear here after each tournament.</p>
        </div>
      ) : (
        groups.map(([eventTitle, eventPhotos]) => (
          <div key={eventTitle} className="recap-event-group">
            <h2 className="recap-event-title">{eventTitle}</h2>
            <div className="recap-masonry">
              {eventPhotos.map((p) => {
                const globalIndex = orderedPhotos.indexOf(p);
                return (
                  <button
                    key={p.id}
                    type="button"
                    className="recap-masonry-item"
                    onClick={() => setLightboxIndex(globalIndex)}
                  >
                    <img
                      src={p.url}
                      alt={p.caption || eventTitle}
                      loading="lazy"
                    />
                    {p.caption && (
                      <span className="recap-masonry-caption">{p.caption}</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))
      )}

      <Lightbox
        open={lightboxIndex >= 0}
        index={lightboxIndex}
        close={() => setLightboxIndex(-1)}
        slides={slides}
        plugins={[Captions, Zoom, Thumbnails]}
        captions={{ descriptionTextAlign: 'center' }}
      />
    </div>
  );
};

export default RecapsPage;
